/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {ColDef} from 'ag-grid-community';
import groupby from 'lodash.groupby';
import isNil from 'lodash.isnil';
import {findAssetEventIsHarsh, getAssetState, getAssetStateLabel} from '@ion/core/src/entities/Asset';
import {translate} from '@ion/core/src/locale/locale';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {EventCodeValueType, getEventName} from '@ion/core/src/services/EventCodes';
import {CVSAssetType, GenericObject, HVSRecord, TripTypeAttribute} from '@ion/core/src/types/entities';
import {getUserUnit, isImperial} from '@ion/core/src/units/helpers';
import {
  dateFormatter,
  DateType,
  timeDiff,
  timeFormatted,
  timeFormatter
} from '@ion/core/src/utils/DateUtil';
import {
  calculateDistance
} from '@ion/core/src/utils/DistanceUtils';
import {
  safeJSONParse
} from '@ion/core/src/utils/JSONUtils';
import {GridResult} from '@ion/dls/src/grid/PaginationControls';
import {dateTimeFormatter, userPreferredTimeFormatter} from '@ion/dls/src/utils/ComponentUtils';

// Defines the report type.
export enum ReportType {
  detail = 'detail',
  summary = 'summary'
}

// defines the enumeration for asset history grid
export enum AssetHistoryFields {
  tripGroup = 'tripGroup',
  ionVision = 'ionVision',
  iOnVisionEvent = 'iOnVisionEvent',
  assetId = 'assetId',
  date = 'date',
  dayTime = 'dayTime',
  VideoURL = 'VideoURL',
}

export enum HiddenHistoryColumns {
  assetId = AssetHistoryFields.assetId,
  tripGroup = AssetHistoryFields.tripGroup,
  dayTime = AssetHistoryFields.dayTime
}

// defines the prop definitions for column chooser params
export interface ColumnChooserParamsType {
  showColumnChooser: boolean;
  columnDefs: ColDef[];
  defaultDefinitions: ColDef[];
  onColumnChooserSave: (columns: ColDef[]) => void;
  onColumnChooserClose: () => void;
};

// defines the params deinition of exportSummaryGroupedReport...
export interface ExportSummaryGroupedReport {
  gridItems: GridResult[];
  includeEvents?: boolean;
  includeGrouped?: boolean;
  includeDetail?: boolean;
  sortOn?: string[]
};

// defines the params deinition of augmentGroupedCSVFormatter...
export interface AugmentGroupedCSVFormatter {
  gridItems: GridResult[];
  reportType: ReportType;
  includeGrouped?: boolean;
  includeDetail?: boolean;
  sortOn?: string[];
};

const getHiddenValue = (): string => translate('reports:hiddenValue');

export const maskPrivateValue = (
  isTripServicesEnabled: boolean,
  tripType: TripTypeAttribute,
  value: any
): any => (
  isTripServicesEnabled && tripType === TripTypeAttribute.Private
    ? getHiddenValue()
    : value
);

/**
 * COLUMN AGGREGATORS
 */

/**
 * General sum aggregator that essentially adds all the values in a column
 * to display the total.
 */
export function sumAggregator(params) {
  let sum = 0;
  params.values.forEach(value => {
    sum += value;
  });
  return sum;
};

/**
 * Takes params from the grid column and sets the time only for the first and
 * last values passed to the aggregator. Returns a translated value for display
 * in the grouped column header.
 */
export function timeAggregator(params: {values: DateType[]}) {
  const {values = []} = params;
  if (values.length) {
    return formatDayTime(values[0], values[values.length - 1]);
  }
};

/**
 * @private
 */
function formatDayTime(startTime: DateType, endTime: DateType): string {
  if (startTime && endTime) {
    const timeFormat = UserPreferences.getSystemTimeFormat();
    return translate('common:time.timeToTime', {
      startTime : timeFormatter(startTime, timeFormat),
      endTime   : timeFormatter(endTime, timeFormat)
    });
  }
}

/**
 * Takes params from the grid column and formats the time only for the first and
 * last values passed to the aggregator.
 */
export function timeFormatAggregator(params: {values: DateType[]}) {
  const {values = []} = params;
  if (values.length) {
    return timeFormatModifier(values[0], values[values.length - 1]);
  }
};

/**
 * Returns a translated value for display in the grouped column header.
 */
function timeFormatModifier(startTime: DateType, endTime: DateType): GenericObject {
  if (startTime && endTime) {
    return {
      from : translate('common:time.fromTime', {time: dateTimeFormatter(startTime, true)}),
      to   : translate('common:time.toTime', {time: dateTimeFormatter(endTime, true)})
    };
  }
};

const exportedTimeFormatModifier = (startTime: DateType, endTime: DateType) => `${dateTimeFormatter(startTime, true)}-${dateTimeFormatter(endTime, true)}`;

/**
 * Simple aggregator that takes the first value in the list as the starting
 * point of a trip.
 */
export function originAggregator(params) {
  return params.values[0];
};

/**
 * Simple aggregator that takes the last value in the list as the destination
 * point of a trip.
 */
export function destinationAggregator(params) {
  return params.values[params.values.length - 1];
};

/**
 * Calculates the distance between the first and last point by the odometer reading
 * and displays that in the user's preferred unit as the odometer reading is being
 * returned in HVS based on the user's units.
 */
export function distanceAggregator(params) {
  const convertedEvents = params.values;
  return getTotalDistance(convertedEvents);
};

/**
 * @private
 */
function getTotalDistance(values) {
  const distanceUOM  = translate(isImperial(getUserUnit())
    ? 'common:distance.milesLower'
    : 'common:distance.kilometersLower'
  );
  if (values?.length >= 2) {
    const diff = calculateDistance(values);
    if (!isNil(diff)) {
      return `${diff} ${distanceUOM}`;
    }
  }
  return '- -';
};

/**
 * Returns an object that gets passed to the cell renderer with a simple array
 * length value as the number of events and a boolean value for if the list includes
 * any harsh events based on the event code.
 */
export function eventsAggregator(params) {
  const harshEvents = params.values.filter(event => findAssetEventIsHarsh(event.eventCode));
  return {
    totalEvents         : params.values.length,
    includesHarshEvents : Boolean(harshEvents.length)
  };
};

/**
 * Simply pulls the driver from the first value in the array since the driver
 * value does not change.
 */
export function driverAggregator(params) {
  return params.values[0];
};

/**
 * Calculates the time between 2 events by returning a formatted, translated,
 * time display.
 */
export function durationAggregator(params) {
  const startEventTime = params.values[0];
  const endEventTime   = params.values[params.values.length - 1];
  return getTotalDuration(startEventTime, endEventTime);
};

/**
 * @private
 */
function getTotalDuration(startEventTime: number, endEventTime: number): string {
  const diff = Math.abs(timeDiff(startEventTime, endEventTime, 's'));
  return timeFormatted(diff, translate);
};

/**
 * Specific calendar date value to be passed to the Chevron Cell renderer
 * to match the row key which is currently set to the calendar date. Each
 * group would have a unique calendar date since they are grouped this way.
 *
 * NOTE: This determines which group to open when a chevron is clicked,
 * so it's tied to how the rows are grouped.
 * If the trip grouping changes, we need to change this.
 */
export function chevronRowMenuAggregator(params) {
  // ** NOTE: This determines which group to open when a chevron is clicked,
  // so it's tied to how the rows are grouped.
  // If the trip grouping changes, we need to change this
  return Math.abs(params.values[0]).toString();
};

/**
 * VALUE FORMATTERS
 */

/**
 * General formatter that only displays the value in the group header and
 * not in any individual cells. The params value is returned from the column's
 * aggregator function.
 */
export function groupOnlyFormatter(params) {
  // we only want to show a value for the group parent
  return params.node.group ? params.value : '';
}

/**
 * Converts the event time number to the time of day for grouping withing the
 * day.
 */
export function dayTimeFormatter(params) {
  const cellValue = params.data ? timeFormatter(params.data.eventTime, UserPreferences.getSystemTimeFormat()) : '';
  return params.node.group ? params.value : cellValue;
}

/**
 * Given any row value it finds the value of the row above it, calculates
 * the difference, then displays it in a formatted value. Uses absolute
 * values to future proof against any reversed orders by sorting that might
 * return negative differences.
 */
export function durationFormatter(params) {
  let diff = '';
  const rowIndex = params.node.rowIndex;
  const rowAbove = params.api.getDisplayedRowAtIndex(rowIndex + 1);
  if (rowAbove?.data) {
    const rowAboveValue = rowAbove.data.eventTime;
    // get absolute number to account for any order
    const diffNumber = Math.abs(timeDiff(params.value, rowAboveValue, 's'));
    diff = timeFormatted(diffNumber, translate);
  }
  return params.node.group ? params.value : diff;
}

/**
 * The method takes the collection of asset service log and converts them into
 * values that can be consumable by the existing export value-filed mapping. This method
 * can produce different outputs based on the report type. If the user want event details alone,
 * report type must be 'detail'. If the user wants summary, then this provides the grouped
 * column alone. By default it provides the same format as in the Asset History report grid.
 *
 * ```typescript
 *
 *  import {augmentCSVFormatter} from './AssetHistoryGridUtils';
 *
 *  const defaultExport = augmentCSVFormatter(gridItems); // default export
 *  const detailExport  = augmentCSVFormatter(gridItems, 'detail'); // detail export
 *  const summaryExport = augmentCSVFormatter(gridItems, 'summary'); // summary export
 *
 * ```
 *
 * @param gridItems  Collection of asset service log that already augmented for grid
 * @param reportType Type of the report.
 * @returns augmented items for CSV export
 */
export function augmentCSVFormatter(gridItems: GridResult[], reportType: ReportType) {
  switch (reportType) {
    case ReportType.detail:
      return augmentEventItems(gridItems, true);
    case ReportType.summary:
      return exportSummaryReport(gridItems, false);
    default:
      return exportSummaryReport(gridItems, true);
  }
};

/**
 * @private
 */
function exportSummaryReport(gridItems: GridResult[], includeEvents: boolean) {
  const augmentItems = [];
  const groupedItems = groupby(gridItems, AssetHistoryFields.tripGroup);
  Object.entries(groupedItems).forEach(([key, value]) => {
    augmentItems.push(augmentGroupedItems(value as GridResult[]));
    includeEvents && augmentItems.push(...augmentEventItems(value as GridResult[], false));
  });
  return augmentItems;
}

/**
 * Defines a utility to return the status of the asset.
 */
export const getStatus = (item: CVSAssetType): string => (
  getAssetStateLabel(getAssetState(item as CVSAssetType))
);

/**
 * @private
 */
function augmentEventItems(items: GridResult[], isDetailView: boolean) {
  return items?.map(item => {
    const {dayTime, eventTime, driver, isTripServicesEnabled, tripType, VideoURL} = item;
    return {
      ...item,
      tripDate         : isDetailView ? dateFormatter(new Date(dayTime), UserPreferences.getSystemDateFormat()) : '',
      VideoURL         : maskPrivateValue(isTripServicesEnabled, tripType, VideoURL),
      dayTime          : maskPrivateValue(isTripServicesEnabled, tripType, dateTimeFormatter(eventTime, true)),
      duration         : '',
      distance         : '',
      accountName      : '',
      assetAlias       : '',
      operatorType     : '',
      operatorCategory : '',
      driver           : isDetailView ? driver : '',
      origin           : '',
      destination      : '',
      event            : transformEventData(item),
      status           : getStatus(item)
    };
  });
}

/**
 * @private
 */
function augmentGroupedItems(items: GridResult[], isGrouped: boolean = false) {
  const eventCount = items.length;
  if (eventCount) {
    const {
      assetName,
      eventTime: startEventTime,
      deviceEsn,
      assetAlias,
      group,
      tripGroup,
      accountName,
      operatorType,
      operatorCategory,
      isTripServicesEnabled,
      tripType,
      origin = '',
      driver = '',
      destination = ''
    } = items[0];
    const {
      eventTime: endEventTime
    } = items[eventCount - 1];

    const distance = getTotalDistance(items.map(e => e.gpsOdometer));
    if (tripGroup) {
      return {
        assetName,
        origin,
        destination,
        accountName,
        driver,
        group,
        deviceEsn,
        assetAlias,
        operatorType,
        operatorCategory,
        distance,
        date    : maskPrivateValue(isTripServicesEnabled, tripType, tripDateFormatter(items, isGrouped)),
        dayTime : maskPrivateValue(
          isTripServicesEnabled,
          tripType,
          exportedTimeFormatModifier(startEventTime, endEventTime)
        ),
        duration : formatDuration(startEventTime, endEventTime),
        event    : translate(`reports:assetHistory.export.${eventCount > 1 ? 'events' : 'event'}`, {count: eventCount})
      };
    }
  }
}

/**
 * @private
 */
function tripDateFormatter(value: GridResult[], isGrouped: boolean = false) {
  const dateFormat = UserPreferences.getSystemDateFormat();
  const firstDate  = dateFormatter(value[0]?.eventTime, dateFormat) || '';
  const lastDate   = dateFormatter(value[value.length - 1]?.eventTime, dateFormat);
  let dateToShow   = firstDate;
  if (lastDate && firstDate !== lastDate) {
    dateToShow = `${firstDate} - ${lastDate}`;
  }
  if (isGrouped) {
    return `${dateToShow} - ${value[0]?.assetName}`;
  }
  return dateToShow;
}

/**
   * @private
   *
   * Filter the Asset history collection with unique messageUuid
   */
export function verifyItems(gridItems): HVSRecord[] {
  return gridItems?.reduce((unique, attr) => {
    if ((!unique.some(obj => obj.messageUuid === attr.messageUuid) || attr.geofenceType)) {
      unique.push(attr);
    }
    return unique;
  }, []);
};

/**
 * The utility method that extract the matched events based on the geofence.
 *
 * ```typescript
 *
 *  import {extractMatchedEvents} from './AssetHistoryGridUtils';
 *
 *  const matchedEvents = extractMatchedEvents(event, eventlist);
 *
 * ```
 *
 * @param actualEvent The actual event that captured.
 * @param allEvents  All events list
 * @returns Matched Event list
 */
export const extractMatchedEvents = (actualEvent: HVSRecord, allEvents: HVSRecord[]) => {
  if (actualEvent?.messageUuid && allEvents?.length) {
    const {messageUuid, eventTime} = actualEvent;
    return allEvents.filter((event) => event.messageUuid === messageUuid || event.eventTime === eventTime);
  }
  return [];
};

/**
 * The utility just return the valid value from the array of values to
 * the trip rows in Asset history Grid and null for other rows.
 */
export const tripRowAggregator = (params: {values: string[], rowNode: GenericObject}) => {
  const {values, rowNode: {field}} = params;
  if (values?.length && field === AssetHistoryFields.tripGroup) {
    return values.find(value => !!value);
  }
};

// Defines a utility to validate a field
export function isField(params, field: string): boolean {
  return params?.rowNode?.field === AssetHistoryFields[field];
};

/**
 * Simple aggregator that takes the first value in the list as the starting
 * point of a trip.
 */
export function originGroupedAggregator(params) {
  return !isField(params, 'assetId') ? params.values[0] : '';
};

/**
 * Simple aggregator that takes the last value in the list as the destination
 * point of a trip.
 */
export function destinationGroupedAggregator(params) {
  return !isField(params, 'assetId') ? params.values[params.values.length - 1] : '';
};

/**
 * Calculates the distance between the first and last point by the odometer reading
 * and displays that in the user's preferred unit as the odometer reading is being
 * returned in HVS based on the user's units.
 */
export function distanceGroupedAggregator(params) {
  return !isField(params, 'assetId') ? getTotalDistance(params.values) : '';
};

/**
 * Returns an object that gets passed to the cell renderer with a simple array
 * length value as the number of events and a boolean value for if the list includes
 * any harsh events based on the event code.
 */
export function eventsGroupedAggregator(params) {
  if (!isField(params, 'assetId')) {
    const harshEvents = params?.rowNode?.allLeafChildren?.filter(eventsHistory => {
      const eventData = eventsHistory?.data;
      return eventData.isSevereEvent;
    });
    return {
      totalEvents         : params.values.length,
      includesHarshEvents : Boolean(harshEvents.length)
    };
  }
  return '';
};

/**
 * Simply pulls the field data from the first value in the array since the field data
 * value does not change.
 */
export function fieldGroupedAggregator(params) {
  return !isField(params, 'assetId') ? params.values[0] : '';
};

/**
 * @private
 */
const pad = (num: number) => {
  return num.toString().padStart(2, '0');
};

/**
 * @private
 */
function formatDuration(start: number, end: number) {
  const difference = Math.abs(start - end);
  const hours = Math.floor(difference / 3600000);
  const minutes = Math.floor((difference % 3600000) / 60000);
  const seconds = Math.floor(((difference % 3600000) % 60000) / 1000);
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

/**
 * Calculates the time between 2 events by returning the difference between the
 * first and last event time to display the duration of the trip.
 */
export function durationGroupedAggregator(params) {
  if (!isField(params, 'assetId')) {
    return formatDuration(params?.values?.[0], params.values?.[params?.values?.length - 1]);
  }
  return '';
};

/**
 * Converts the event time number to the time of day for grouping withing the
 * day.
 */
export function dayTimeGroupedFormatter(params) {
  const {node, data} = params;
  if (node.field === AssetHistoryFields.assetId) {
    return '';
  } else if (node.field === AssetHistoryFields.tripGroup) {
    return node.aggData.dayTime;
  } else {
    return userPreferredTimeFormatter(params.data.eventTime);
  }
}

/**
 * The method takes the collection of asset service log and converts them into
 * values that can be consumable by the existing export value-filed mapping. This method
 * can produce different outputs based on the report type. If the user want event details alone,
 * report type must be 'detail'. If the user wants summary, then this provides the grouped
 * column alone. By default it provides the same format as in the Asset History report grid.
 *
 * ```typescript
 *
 *  import {augmentGroupedCSVFormatter} from './AssetHistoryGridUtils';
 *
 *  const defaultExport = augmentGroupedCSVFormatter(gridItems); // default export
 *  const detailExport  = augmentGroupedCSVFormatter(gridItems, 'detail'); // detail export
 *  const summaryExport = augmentGroupedCSVFormatter(gridItems, 'summary'); // summary export
 *
 * ```
 *
 * @param gridItems  Collection of asset servcie log that already augmented for grid
 * @param reportType Type of the report.
 * @returns augmented items for CSV export
 */
export const augmentGroupedCSVFormatter = ({
  gridItems,
  reportType,
  sortOn,
  includeGrouped = true,
  includeDetail = false
}: AugmentGroupedCSVFormatter) => {
  switch (reportType) {
    case ReportType?.detail:
      return augmentEventItems(gridItems, true);
    case ReportType?.summary:
      return exportSummaryGroupedReport({
        includeEvents : false,
        includeGrouped,
        includeDetail,
        gridItems,
        sortOn
      });
    default:
      return exportSummaryGroupedReport({
        includeEvents : true,
        includeGrouped,
        includeDetail,
        sortOn,
        gridItems
      });
  }
};

/**
 * @private
 */
const exportSummaryGroupedReport = ({
  gridItems,
  includeEvents,
  sortOn,
  includeGrouped = true,
  includeDetail = false
}: ExportSummaryGroupedReport) => {
  const augmentItems = [];
  let tripGroupRef = 0;

  const isDescSort = sortOn[0] === '-ag-Grid-AutoColumn';

  const groupedItems = gridItems.reduce((result, item) => {
    const itemAssetId   = item[AssetHistoryFields.assetId];
    let itemTripGroup = item[AssetHistoryFields.tripGroup];
    !itemTripGroup ? itemTripGroup = tripGroupRef + 1 : tripGroupRef = itemTripGroup;
    if (item?.[AssetHistoryFields.date] && item.eventTime) {
      item[AssetHistoryFields.date] = dateFormatter(new Date(item.eventTime), UserPreferences.getSystemDateFormat());
    }
    return {
      ...result,
      [itemAssetId] : {
        ...result[itemAssetId],
        [itemTripGroup] : [
          ...((result[itemAssetId]?.[itemTripGroup]) || []),
          item
        ]
      }
    };
  }, {});

  Object.entries(groupedItems).forEach(([assetId, trips]) => {
    const sortedTrips = isDescSort ? Object.entries(trips).reverse() : Object.entries(trips);
    sortedTrips.forEach(([tripNumber, tripArray]) => {
      const sortedTripArray = isDescSort ? tripArray.reverse() : tripArray;
      if (includeGrouped) {
        const groupRow = augmentGroupedItems(sortedTripArray as GridResult[]);
        groupRow && augmentItems.push(groupRow);
      }
      includeEvents && augmentItems.push(...augmentEventItems(sortedTripArray as GridResult[], includeDetail));
    });
  });
  return augmentItems;
};

/**
 * The getAHREventName utility provides a way to construct the event name based on the
 * event id and the geofence type of the event.
 *
 * @param eventCode    Code of the event.
 * @param geofenceType Geofence type of the event
 * @returns AHR event name based on the event
 *
 * ```typescript
 *
 *   import {getAHREventName} from '@ion/features/src/asset-history';
 *
 *   const eventName = getAHREventName(eventCode, geofenceType);
 *
 * ```
 */
export const getAHREventName = (eventCode: number, geofenceType?: string) => {
  if (!geofenceType) {
    return getEventName({from: EventCodeValueType.eventCode, val: eventCode});
  }
  return translate(`event-codes:geofenceEvents.${geofenceType.toLowerCase()}`);
};

/**
 * Retrieves the events within the inputChanged object and returns the events excluding any ignition events.
 *
 * ```typescript
 *
 * import {getInputChangedEvents} from '@ion/features/src/asset-history';
 *
 * eventTypeUI: getInputChangedEvents(event);
 *
 * ```
 *
 */
export const getInputChangedEvents = (event: GenericObject) => {
  const {inputChanged} = event || {};
  if (!isNil(inputChanged)) {
    const inputEvents = [];
    const parsedEvent = safeJSONParse(inputChanged);
    for (const key in parsedEvent) {
      if (key !== 'ignition') {
        inputEvents.push(`${key} ${parsedEvent[key]}`);
      }
    }
    return inputEvents.join(', ');
  }
};

/**
 * Transforms the event if the inputChanged object contains events.
 * If the inputChanged object does not have events to show, then event name based on the event code.
 *
 * ```typescript
 *
 * import {transformEventData} from '@ion/features/src/asset-history';
 *
 * eventTypeUI: transformEventData(event);
 *
 * ```
 *
 * @param event Event object
 * @returns multiple events or event code
 */
export const transformEventData = (event: GenericObject) => {
  const {inputChanged, eventCode, geofenceType} = event || {};
  if (!isNil(inputChanged) && inputChanged !== 'null') {
    return getInputChangedEvents(event);
  }
  return getAHREventName(eventCode, geofenceType);
};

const getTripNodeFirstDayTime = (node, value) => {
  return node.group ? node.allLeafChildren[0].data.eventTime : value;
};

// eslint-disable-next-line max-params
export const groupedColumnSorter = (valueA, valueB, nodeA, nodeB) => {
  if (nodeA.field !== AssetHistoryFields.assetId && nodeB.field !== AssetHistoryFields.assetId) {
    const newValueA = getTripNodeFirstDayTime(nodeA, valueA);
    const newValueB = getTripNodeFirstDayTime(nodeB, valueB);
    const timeDiff  = newValueA - newValueB;
    const eventA    = nodeA?.data;
    const eventB    = nodeB?.data;
    if (timeDiff === 0 && eventA && eventB) {
      return (
        eventA.deviceMessageSequenceNumber - eventB.deviceMessageSequenceNumber
      );
    }
    return newValueA - newValueB;
  }
};

export const resolvePersistedColumnState = (columns) => {
  const resolvedColumns = columns?.map((column) => {
    if (HiddenHistoryColumns[column.colId]) {
      return {
        ...column,
        hide : true
      };
    } else {
      return column;
    }
  });
  return resolvedColumns;
};
